import { AlertTriangle, Lock, RefreshCw, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Return a best-effort "Complete Payment" URL:
 *   1. Stripe hosted invoice URL (from the last failed invoice) if we have it.
 *   2. Fallback to /plan-selection so the client can re-subscribe.
 */
export function getCompletePaymentHref(billing) {
  if (billing?.last_failed_invoice_url) return billing.last_failed_invoice_url;
  return '/plan-selection';
}

/**
 * Fullscreen-ish blocker rendered in the dashboard main area when access is
 * restricted. Sidebar still shows Billing & Plan + Logout so the client can
 * reach the billing page.
 */
export default function AccessGate({ state, billing }) {
  if (state === 'no_plan') {
    // No-plan clients now keep full dashboard access; the prompt lives in
    // <NoPlanBanner> at the top of the dashboard. Render nothing here.
    return null;
  }
  if (state === 'cancelled') {
    return (
      <BlockerCard
        icon={<Lock className="w-8 h-8 text-white" />}
        title="Your subscription is no longer active"
        subtitle="Choose a plan to continue using your Law Agency 360 dashboard."
        ctaLabel="Choose a plan to continue"
        ctaHref="/plan-selection"
        testId="access-gate-cancelled"
      />
    );
  }
  if (state === 'past_due_locked') {
    return (
      <BlockerCard
        icon={<AlertTriangle className="w-8 h-8 text-white" />}
        title="Complete the payment to resume access"
        subtitle="Your recent payment could not be completed. Please complete the payment to continue using your dashboard."
        ctaLabel="Complete Payment"
        ctaHref={getCompletePaymentHref(billing)}
        ctaExternal={!!billing?.last_failed_invoice_url}
        testId="access-gate-past-due-locked"
        secondary={{ label: 'Go to Billing & Plan', href: '/dashboard/billing' }}
      />
    );
  }
  return null;
}

function BlockerCard({ icon, title, subtitle, ctaLabel, ctaHref, ctaExternal = false, secondary, testId }) {
  const ctaClass =
    'inline-flex items-center gap-2 justify-center w-full sm:w-auto px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-violet-600/30 transition-all';
  return (
    <div
      className="min-h-[calc(100vh-10rem)] flex items-center justify-center px-4"
      data-testid={testId}
    >
      <div className="max-w-xl w-full text-center bg-white/70 backdrop-blur-sm border border-white/50 rounded-3xl p-10 soft-shadow">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center mb-6 shadow-lg shadow-violet-500/30">
          {icon}
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>
          {title}
        </h1>
        <p className="text-slate-600 mb-8">{subtitle}</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
          {ctaExternal ? (
            <a href={ctaHref} className={ctaClass} data-testid={`${testId}-cta`}>
              {ctaLabel}
            </a>
          ) : (
            <Link to={ctaHref} className={ctaClass} data-testid={`${testId}-cta`}>
              {ctaLabel}
            </Link>
          )}
          {secondary && (
            <Link
              to={secondary.href}
              className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 rounded-xl font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
              data-testid={`${testId}-secondary`}
            >
              {secondary.label}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/** Sticky-style top banner shown across the dashboard when a client has no
 *  active plan. Drives upgrade without blocking navigation. */
export function NoPlanBanner() {
  return (
    <div
      className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 sm:py-5 rounded-2xl bg-gradient-to-r from-violet-700 via-violet-600 to-indigo-700 border border-white/15 shadow-lg shadow-violet-900/30"
      data-testid="no-plan-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
          <CreditCard className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="text-sm sm:text-base font-bold text-white leading-snug">
            Choose a plan to get started - unlock your full dashboard and start receiving work.
          </div>
          <p className="text-xs sm:text-sm text-violet-50/90 mt-1">
            You can explore the dashboard, but services activate after selecting a plan.
          </p>
        </div>
      </div>
      <Link
        to="/plan-selection"
        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white text-violet-700 text-sm font-bold hover:shadow-lg hover:shadow-black/30 hover:bg-violet-50 transition-all sm:shrink-0 whitespace-nowrap"
        data-testid="no-plan-banner-cta"
      >
        Choose a plan
      </Link>
    </div>
  );
}

/** Warning banner rendered at the top of the dashboard during the grace period. */
export function PastDueBanner({ billing, graceDaysLeft }) {
  const href = getCompletePaymentHref(billing);
  const isExternal = !!billing?.last_failed_invoice_url;
  return (
    <div
      className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 rounded-2xl bg-amber-50 border border-amber-200"
      data-testid="past-due-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <div className="text-sm font-semibold text-amber-900">
            Payment failed. Please update your payment to avoid service interruption.
          </div>
          <p className="text-xs text-amber-700 mt-0.5">
            {graceDaysLeft > 0
              ? `Your dashboard stays active for ${graceDaysLeft} more day${graceDaysLeft === 1 ? '' : 's'} while we retry the charge.`
              : 'Dashboard access will be restricted soon if payment is not completed.'}
          </p>
        </div>
      </div>
      <div className="flex gap-2 sm:shrink-0">
        {isExternal ? (
          <a
            href={href}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold hover:shadow-lg hover:shadow-violet-600/30 transition-all"
            data-testid="past-due-banner-cta"
          >
            <RefreshCw className="w-4 h-4" /> Complete Payment
          </a>
        ) : (
          <Link
            to={href}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold hover:shadow-lg hover:shadow-violet-600/30 transition-all"
            data-testid="past-due-banner-cta"
          >
            <RefreshCw className="w-4 h-4" /> Complete Payment
          </Link>
        )}
        <Link
          to="/dashboard/billing"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-amber-200 text-amber-800 text-sm font-semibold hover:bg-amber-50 transition-all"
          data-testid="past-due-banner-billing"
        >
          Billing & Plan
        </Link>
      </div>
    </div>
  );
}
